'use client'

import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { Globe, Check } from 'lucide-react'
import { getFlagByCode } from './flags'

const languages = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' },
  { code: 'fr', name: 'Français' },
  { code: 'sv', name: 'Svenska' },
  { code: 'de', name: 'Deutsch' },
  { code: 'id', name: 'Bahasa Indonesia' },
  { code: 'zh-CN', name: '简体中文' },
  { code: 'ko', name: '한국어' },
  { code: 'ru', name: 'Русский' },
]

export default function LanguagePicker() {
  const { i18n } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0]
  const CurrentFlag = getFlagByCode(currentLanguage.code)

  const handleLanguageChange = async (languageCode: string) => {
    try {
      if (!i18n.hasResourceBundle(languageCode, 'translation')) {
        const res = await fetch(`/locales/${languageCode}.json`)
        if (res.ok) {
          const data = await res.json()
          i18n.addResourceBundle(languageCode, 'translation', data, true, true)
        }
      }
    } catch (err) {
      console.warn('Failed to load locale bundle dynamically:', err)
    }
    await i18n.changeLanguage(languageCode)
    setIsOpen(false)
  }

  return (
    <div className="relative">
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/80 backdrop-blur-sm border border-slate-200 hover:border-emerald-300 shadow-lg hover:shadow-xl transition-all duration-200"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <span className="inline-flex items-center"><CurrentFlag className="w-6 h-4" /></span>
        <span className="text-sm font-medium text-slate-700 hidden sm:block">
          {currentLanguage.name}
        </span>
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[60]"
              onClick={() => setIsOpen(false)}
            />

            {/* Dropdown */}
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="absolute top-full right-0 mt-2 w-48 bg-white rounded-xl shadow-2xl border border-slate-200 z-[70] overflow-hidden"
            >
              {languages.map((language) => {
                const Flag = getFlagByCode(language.code)
                return (
                  <motion.button
                    key={language.code}
                    onClick={() => handleLanguageChange(language.code)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-emerald-50 transition-colors duration-200"
                    whileHover={{ x: 4 }}
                  >
                    <span className="inline-flex items-center"><Flag className="w-6 h-4" /></span>
                    <span className="flex-1 text-sm font-medium text-slate-700">
                      {language.name}
                    </span>
                    {i18n.language === language.code && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="text-emerald-600"
                      >
                        <Check className="w-4 h-4" />
                      </motion.div>
                    )}
                  </motion.button>
                )
              })}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div >
  )
}
